import React from 'react';
import clsx from 'clsx';
import { BarChart3, Package, ShoppingCart, Tag, MapPin, Store, ChevronLeft } from 'lucide-react';
import { useVocab, useIsAppointments } from '../../../hooks/useVocab';

// Quick-action buttons. Labels/visibility adapt to the offering type.
const buildActions = (t, isAppt) => [
    { tabId: 'analytics', label: 'ניתוח נתונים', Icon: BarChart3, tone: 'warning' },
    { tabId: 'products', label: t.catalogManage, Icon: Package, tone: 'brand' },
    { tabId: 'orders', label: t.dashboardOrdersTab, Icon: ShoppingCart, tone: 'success' },
    { tabId: 'categories', label: 'קטגוריות', Icon: Tag, tone: 'info' },
    // A practice has no physical delivery points.
    ...(isAppt ? [] : [{ tabId: 'delivery', label: 'נקודות חלוקה', Icon: MapPin, tone: 'brand' }]),
    { tabId: 'store-details', label: 'הגדרות חנות', Icon: Store, tone: 'brand' },
];

const TONES = {
    brand:   'bg-brand-100 text-brand-700',
    success: 'bg-success-100 text-success-700',
    info:    'bg-info-100 text-info-700',
    warning: 'bg-warning-100 text-warning-700',
};

/**
 * Dashboard quick-action grid — six buttons that jump to admin tabs.
 */
export function DashboardQuickActions({ onGoToTab }) {
    const t = useVocab();
    const isAppt = useIsAppointments();
    const actions = buildActions(t, isAppt);
    return (
        <div className="surface-card p-5">
            <div className="flex items-center justify-between mb-4">
                <h3 className="heading-sm">פעולות מהירות</h3>
                <span className="hidden sm:inline text-xs text-ink-subtle">טיפ: Alt+R לרענון מהיר</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {actions.map(({ tabId, label, Icon, tone }) => (
                    <button
                        key={tabId}
                        type="button"
                        onClick={() => onGoToTab?.(tabId)}
                        className={clsx(
                            'group flex items-center gap-3 p-3 rounded-xl border border-line bg-surface text-start',
                            'hover:border-brand-300 hover:bg-brand-50/50 hover:shadow-xs',
                            'transition-[background-color,border-color,box-shadow] duration-200',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
                        )}
                    >
                        <span className={clsx('inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0 transition-transform duration-200 group-hover:scale-105', TONES[tone])}>
                            <Icon className="w-5 h-5" />
                        </span>
                        <span className="flex-1 font-semibold text-sm text-ink leading-tight">{label}</span>
                        <ChevronLeft className="w-4 h-4 text-ink-faint shrink-0 transition-[color,transform] duration-200 group-hover:text-brand-600 group-hover:-translate-x-0.5" />
                    </button>
                ))}
            </div>
        </div>
    );
}

export default DashboardQuickActions;
